/**
 * AccountBalanceCard — 1:1 web port of
 * `apps/WunderGuide/components/settingsDrawer/AccountBalanceCard.tsx`.
 *
 * Same three visual modes:
 *   1. Unmetered — ∞ icon + "UNLIMITED ACCESS" + ∞ + "Active pass" badge
 *   2. Allowance/quota — circular ring + period title + remaining/total + reset
 *   3. No quota — vertical layout with big balance, optional MM:SS live countdown
 *
 * Same props shape as RN, same field names — tries to keep visual & data
 * parity easy to verify by diffing the two files.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { formatTimeRemaining } from '../../types/allowance';
import './AccountBalanceCard.css';

export interface AccountBalanceCardProps {
  billingEnabled?: boolean;
  balance: number | null;
  hasUnmeteredAccess: boolean;
  isCriticalBalance: boolean;
  isLowBalance: boolean;
  earliestExpiry?: string | Date | null;
  allowance: {
    remaining: number;
    amount: number;
    periodName?: string;
    resetsAt: Date | string;
    stackedCount?: number;
  } | null;
  boostCredits?: number;
  limitedCredits?: number;
  isSessionActive?: boolean;
}

const COUNTDOWN_THRESHOLD = 300;

const AccountBalanceCard: React.FC<AccountBalanceCardProps> = ({
  billingEnabled = true,
  balance,
  hasUnmeteredAccess,
  isCriticalBalance,
  isLowBalance,
  earliestExpiry,
  allowance,
  boostCredits,
  limitedCredits,
  isSessionActive = false,
}) => {
  if (!billingEnabled) return null;

  const showCountdown =
    balance !== null &&
    balance < COUNTDOWN_THRESHOLD &&
    balance > 0 &&
    isSessionActive;
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!showCountdown || balance === null) {
      setCountdown(null);
      return;
    }
    setCountdown(prev => {
      if (prev !== null && balance !== null && Math.abs(prev - balance) < 2) {
        return prev;
      }
      return balance ?? 0;
    });
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showCountdown, balance]);

  const displayBalance = useMemo(() => {
    if (hasUnmeteredAccess) return '∞';
    if (countdown !== null && countdown > 0) return formatCountdown(countdown);
    return balance !== null ? formatTimeRemaining(balance) : 'N/A';
  }, [hasUnmeteredAccess, balance, countdown]);

  /* ── Mode 1: Unmetered ──────────────────────────────────────────── */
  if (hasUnmeteredAccess) {
    return (
      <div className="wg-balance-card">
        <InfinityIcon />
        <div className="wg-balance-card__label">Unlimited access</div>
        <div className="wg-balance-card__value">∞</div>
        <span className="wg-balance-card__pass-badge">
          <InfinityIconSm /> Active pass
        </span>
      </div>
    );
  }

  /* ── Mode 2: Allowance/Quota ────────────────────────────────────── */
  if (allowance) {
    const pct = (allowance.remaining / allowance.amount) * 100;
    const resetsAt =
      allowance.resetsAt instanceof Date
        ? allowance.resetsAt
        : new Date(allowance.resetsAt);
    const resetText = formatResetTime(resetsAt);

    return (
      <div className="wg-balance-card">
        <div className="wg-balance-card__quota">
          <div className="wg-balance-card__ring-wrap">
            <CircularProgress percentage={pct} size={70} stroke={6} />
            <div className="wg-balance-card__ring-inner">
              {formatTimeRemaining(allowance.remaining)}
            </div>
          </div>
          <div className="wg-balance-card__quota-info">
            <div className="wg-balance-card__quota-title">
              {periodLabel(allowance.periodName)}
            </div>
            <div className="wg-balance-card__quota-sub">
              {formatTimeRemaining(allowance.remaining)}
              {' / '}
              {formatTimeRemaining(allowance.amount)}
              {allowance.stackedCount && allowance.stackedCount > 1 && (
                <span className="wg-balance-card__stacked">
                  {' '}
                  ({allowance.stackedCount} passes active)
                </span>
              )}
            </div>
            {resetText && (
              <div className="wg-balance-card__quota-reset">
                Resets in {resetText}
              </div>
            )}
            {isCriticalBalance && (
              <div className="wg-balance-card__warn">
                ⚠️ Voice time critically low
              </div>
            )}
            {isLowBalance && !isCriticalBalance && (
              <div className="wg-balance-card__info">Balance running low</div>
            )}
          </div>
        </div>

        {boostCredits != null && boostCredits > 0 && (
          <>
            <div className="wg-balance-card__divider" />
            <div className="wg-balance-card__credits">
              <div className="wg-balance-card__credits-head">
                <BoltIcon />
                <span>Boost credits</span>
              </div>
              <div className="wg-balance-card__credits-value">
                {formatTimeRemaining(boostCredits)}
              </div>
              <div className="wg-balance-card__credits-expiry">
                Never expires
              </div>
            </div>
          </>
        )}

        {limitedCredits != null && limitedCredits > 0 && (
          <>
            <div className="wg-balance-card__divider" />
            <div className="wg-balance-card__credits">
              <div className="wg-balance-card__credits-head">
                <ClockIcon />
                <span>Limited-time credits</span>
              </div>
              <div className="wg-balance-card__credits-value">
                {formatTimeRemaining(limitedCredits)}
              </div>
              {earliestExpiry && (
                <div className="wg-balance-card__credits-expiry">
                  Expires {formatExpiry(earliestExpiry)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  /* ── Mode 3: No quota — Balance only ────────────────────────────── */
  return (
    <div
      className={`wg-balance-card${isCriticalBalance ? ' is-critical' : ''}`}
    >
      {isCriticalBalance ? <WarningIcon /> : <ClockIconLg />}
      <div className="wg-balance-card__label">Available voice time</div>
      <div
        className={`wg-balance-card__value${
          showCountdown ? ' is-countdown' : ''
        }`}
      >
        {displayBalance}
      </div>
      {showCountdown && (
        <span className="wg-balance-card__live-badge">
          <DotIcon /> Live countdown
        </span>
      )}
      {isCriticalBalance && !showCountdown && (
        <div className="wg-balance-card__warn">
          ⚠️ Voice time critically low
        </div>
      )}
      {isLowBalance && !isCriticalBalance && (
        <div className="wg-balance-card__info">Balance running low</div>
      )}
      {earliestExpiry && (
        <>
          <div className="wg-balance-card__divider" />
          <div className="wg-balance-card__credits-expiry">
            Expires {formatExpiry(earliestExpiry)}
          </div>
        </>
      )}
    </div>
  );
};

export default AccountBalanceCard;

/* ── Helpers ──────────────────────────────────────────────────────── */

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function periodLabel(periodName?: string): string {
  switch ((periodName || '').toLowerCase()) {
    case 'daily':
      return 'Daily quota';
    case 'weekly':
      return 'Weekly quota';
    case 'monthly':
      return 'Monthly quota';
    default:
      return 'Quota';
  }
}

function formatResetTime(d: Date): string | null {
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatExpiry(when: string | Date): string {
  const d = when instanceof Date ? when : new Date(when);
  return formatResetTime(d) || 'soon';
}

/* ── CircularProgress ─────────────────────────────────────────────── */

const CircularProgress: React.FC<{
  percentage: number;
  size?: number;
  stroke?: number;
}> = ({ percentage, size = 70, stroke = 6 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, percentage));
  const dash = (pct / 100) * c;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      className="wg-balance-card__ring"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#fff"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
};

/* ── Inline icons ─────────────────────────────────────────────────── */

function InfinityIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18.6 6.6c2 0 3.6 1.7 3.6 3.7s-1.6 3.7-3.6 3.7-3.7-1.6-7.4-7.4C7.6 11.4 6 13 4 13S.4 11.3.4 9.3 2 5.6 4 5.6c2 0 3.7 1.6 7.4 7.4 3.5 5.8 5.2 7.4 7.2 7.4z" />
    </svg>
  );
}
function InfinityIconSm() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18.6 6.6c2 0 3.6 1.7 3.6 3.7s-1.6 3.7-3.6 3.7-3.7-1.6-7.4-7.4C7.6 11.4 6 13 4 13S.4 11.3.4 9.3 2 5.6 4 5.6c2 0 3.7 1.6 7.4 7.4 3.5 5.8 5.2 7.4 7.2 7.4z" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}
function ClockIconLg() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function WarningIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10.3 3.86a2 2 0 0 1 3.4 0l8.5 14.5A2 2 0 0 1 20.5 21h-17a2 2 0 0 1-1.7-2.64z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function DotIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
      <circle cx="5" cy="5" r="3" fill="currentColor" />
    </svg>
  );
}
